import React from 'react'

import { Box, Grid, LinearProgress, Paper, Skeleton, Typography } from '@mui/material'

import { joinStringsWithBullet } from '../../../../../../shared/functions/String/string.functions'
import theme from '../../../../../../shared/styles/themes/default.theme'
import styles from './MetricsSection.styles'
import { MetricsSectionProps } from './MetricsSection.types'

const MetricsSection: React.FC<MetricsSectionProps> = ({ metrics, loading }) => {
  if (loading) {
    return (
      <Box sx={styles.wrapper}>
        <Grid container spacing={2}>
          {[...Array(3)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={`status-skeleton-${index}`}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          {[...Array(4)].map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={`activity-skeleton-${index}`}>
              <Skeleton variant="rounded" height={110} />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  if (!metrics) {
    return (
      <Box sx={styles.wrapper}>
        <Typography variant="body2" color="text.secondary">
          Locker metrics will appear once SmartLocker data is available.
        </Typography>
      </Box>
    )
  }

  const statusMetrics = [
    { label: 'Available', data: metrics.available, color: theme.palette.success.main },
    { label: 'Reserved', data: metrics.reserved, color: theme.palette.info.main },
    { label: 'Occupied', data: metrics.occupied, color: theme.palette.primary.main }
  ]

  return (
    <Box sx={styles.wrapper}>
      <Grid container spacing={2}>
        {statusMetrics.map((metric) => (
          <Grid item xs={12} sm={6} md={4} key={metric.label}>
            <Paper sx={styles.statusCard(metric.color)} elevation={0}>
              <Typography variant="subtitle2" color="text.secondary">
                {metric.label}
              </Typography>

              <Typography variant="h4" sx={{ mt: 1 }}>
                {metric.data.count}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {metric.data.percent.toFixed(0)}% of total boxes
              </Typography>

              <LinearProgress
                variant="determinate"
                value={Math.min(metric.data.percent, 100)}
                sx={styles.progressBar(metric.color)}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={styles.secondaryCard} elevation={0}>
            <Typography variant="subtitle2" color="text.secondary">
              Deliveries
            </Typography>

            <Typography variant="body1">
              {joinStringsWithBullet([metrics.deliveredDaily.toString(), 'today'])}
            </Typography>

            <Typography variant="body1">
              {joinStringsWithBullet([metrics.deliveredMonthly.toString(), 'this month'])}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={styles.secondaryCard} elevation={0}>
            <Typography variant="subtitle2" color="text.secondary">
              Pickups
            </Typography>

            <Typography variant="body1">
              {joinStringsWithBullet([metrics.pickedUpDaily.toString(), 'today'])}
            </Typography>

            <Typography variant="body1">
              {joinStringsWithBullet([metrics.pickedUpMonthly.toString(), 'this month'])}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={styles.secondaryCard} elevation={0}>
            <Typography variant="subtitle2" color="text.secondary">
              Average Dwell Time
            </Typography>

            <Typography variant="h5">
              {metrics.averageDwellTime} day{metrics.averageDwellTime > 1 ? 's' : ''}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              From drop-off to pickup time
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={styles.secondaryCard} elevation={0}>
            <Typography variant="subtitle2" color="text.secondary">
              Overdue Pickups
            </Typography>

            <Typography variant="h5">{metrics.overduePickups}</Typography>

            <Typography variant="body2" color="text.secondary">
              Boxes exceeding 48 hours
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default MetricsSection
